import type { NoiseReducer, NoiseReductionProvider } from "./NoiseReducer";
import { audioContextManager } from "../AudioContextManager";
import { AbortError } from "@workadventure/shared-utils/src/Abort/AbortError";
import rnnoiseWorkletUrl from "./RNNoiseWorkletProcessor.ts?worker&url";

/** Sample rate required by RNNoise. */
const RNNOISE_SAMPLE_RATE = 48000;

/** Path to the pre-compiled WASM binary served as a static asset. */
const RNNOISE_WASM_URL = "/static/rnnoise/rnnoise.wasm";

interface WorkletReadyMessage {
    type: "ready";
}
interface WorkletErrorMessage {
    type: "error";
    message: string;
}
type WorkletMessage = WorkletReadyMessage | WorkletErrorMessage;

/**
 * AudioWorklet-based noise reducer using the RNNoise WASM model.
 *
 * Pipeline per stream:
 *   MediaStreamAudioSourceNode
 *     -> AudioWorkletNode (rnnoise-worklet-processor)
 *       -> MediaStreamAudioDestinationNode
 *
 * The WASM binary is fetched once on the main thread and transferred to the
 * worklet via MessagePort (no SharedArrayBuffer required).
 */
export class RNNoiseWasmReducer implements NoiseReducer {
    private static instanceCounter = 0;
    private static wasmBinaryPromise: Promise<ArrayBuffer> | null = null;
    private static readonly workletLoadPromises = new WeakMap<AudioContext, Promise<void>>();

    private readonly instanceId = ++RNNoiseWasmReducer.instanceCounter;
    private audioContext: AudioContext | null = null;
    private workletNode: AudioWorkletNode | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private destinationNode: MediaStreamAudioDestinationNode | null = null;
    private wasmBinary: ArrayBuffer | null = null;
    private outputTrack: MediaStreamTrack | null = null;
    private pendingInitCancel: ((reason?: Error) => void) | null = null;
    private closed = false;

    private log(message: string, details?: unknown): void {
        if (details === undefined) {
            console.debug(`[RNNoiseWasmReducer#${this.instanceId}] ${message}`);
            return;
        }

        console.debug(`[RNNoiseWasmReducer#${this.instanceId}] ${message}`, details);
    }

    async init(): Promise<void> {
        this.log("Init start");

        this.closed = false;
        this.wasmBinary = await RNNoiseWasmReducer.getWasmBinary();
        this.audioContext = audioContextManager.getContext(RNNOISE_SAMPLE_RATE);
        await this.audioContext.resume();
        await RNNoiseWasmReducer.ensureWorkletLoaded(this.audioContext);

        this.log("Init end", { audioContextState: this.audioContext.state });
    }

    async processStream(input: MediaStream): Promise<MediaStream> {
        if (this.closed) {
            throw new Error("[RNNoiseWasmReducer][ProcessStream][ERROR] Reducer already closed");
        }
        if (!this.audioContext || this.wasmBinary === null) {
            throw new Error("[RNNoiseWasmReducer][ProcessStream][ERROR] Reducer not initialized");
        }

        // Clean up any previously created nodes for a different stream.
        this.disconnectNodes();

        const audioTracks = input.getAudioTracks();
        if (audioTracks.length === 0) {
            // No audio tracks to process; return the stream unchanged.
            return input;
        }

        // Build the Web Audio processing chain.
        const audioOnlyStream = new MediaStream(audioTracks);
        this.sourceNode = this.audioContext.createMediaStreamSource(audioOnlyStream);
        this.destinationNode = this.audioContext.createMediaStreamDestination();

        const workletNode = new AudioWorkletNode(this.audioContext, "rnnoise-worklet-processor", {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            outputChannelCount: [1],
        });
        this.workletNode = workletNode;

        // Wait for the worklet to signal ready after WASM init.
        await this.initWorkletWasm(workletNode);

        if (this.closed || this.workletNode !== workletNode || !this.destinationNode || !this.sourceNode) {
            throw new Error("[RNNoiseWasmReducer][ProcessStream][ERROR] Reducer closed during worklet initialization");
        }

        this.sourceNode.connect(workletNode);
        workletNode.connect(this.destinationNode);

        // Combine processed audio track with original video tracks.
        const processedAudioTrack = this.destinationNode.stream.getAudioTracks()[0];
        this.outputTrack = processedAudioTrack ?? null;

        const outputStream = new MediaStream();
        if (processedAudioTrack) {
            outputStream.addTrack(processedAudioTrack);
        }
        for (const track of input.getVideoTracks()) {
            outputStream.addTrack(track);
        }

        this.log(
            "ProcessStream end",
            {
                audioTracks: outputStream.getAudioTracks().length,
                videoTracks: outputStream.getVideoTracks().length,
            }
        );

        return outputStream;
    }

    /** Transfers the WASM binary to the worklet and waits for it to be ready. */
    private initWorkletWasm(workletNode: AudioWorkletNode): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this.wasmBinary) {
                reject(new Error("[RNNoiseWasmReducer][InitWorklet][ERROR] workletNode or wasmBinary is null"));
                return;
            }

            let settled = false;
            const cleanup = () => {
                clearTimeout(timeoutId);
                workletNode.port.onmessage = null;
                if (this.pendingInitCancel === cancelInit) {
                    this.pendingInitCancel = null;
                }
            };
            const cancelInit = (reason?: Error) => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                reject(reason ?? new AbortError("[RNNoiseWasmReducer][InitWorklet][ERROR] Worklet init cancelled"));
            };

            const timeoutId = setTimeout(() => {
                cancelInit(new Error("[RNNoiseWasmReducer][InitWorklet][ERROR] Worklet init timed out after 10s"));
            }, 10_000);
            this.pendingInitCancel = cancelInit;

            workletNode.port.onmessage = (event: MessageEvent<WorkletMessage>) => {
                if (event.data.type === "ready") {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    cleanup();
                    resolve();
                } else if (event.data.type === "error") {
                    cancelInit(new Error(`[RNNoiseWasmReducer][InitWorklet][ERROR] ${event.data.message}`));
                }
            };

            // Slice a copy for transfer; the original ArrayBuffer is kept intact
            // for future processStream calls after device or permission changes.
            const wasmCopy = this.wasmBinary.slice(0);
            workletNode.port.postMessage({ type: "init", wasmBinary: wasmCopy }, [wasmCopy]);
        });
    }

    private disconnectNodes(options?: { stopOutputTrack?: boolean }): void {
        this.pendingInitCancel?.(new AbortError("[RNNoiseWasmReducer][Disconnect][ERROR] Worklet init cancelled"));
        try {
            if (this.workletNode) {
                this.workletNode.port.postMessage({ type: "close" });
                this.workletNode.port.onmessage = null;
                this.workletNode.disconnect();
                this.workletNode = null;
            }
            if (this.sourceNode) {
                this.sourceNode.disconnect();
                this.sourceNode = null;
            }
            if (this.destinationNode) {
                this.destinationNode.disconnect();
                this.destinationNode = null;
            }
            if (options?.stopOutputTrack && this.outputTrack) {
                this.outputTrack.stop();
            }
        } catch (err) {
            console.warn(`[RNNoiseWasmReducer#${this.instanceId}] Disconnect warn`, err);
        }

        this.outputTrack = null;
    }

    close(): void {
        this.log("Close start");
        this.closed = true;
        this.disconnectNodes({ stopOutputTrack: true });
        this.wasmBinary = null;
        this.audioContext = null;
        this.log("Close end");
    }

    getProvider(): NoiseReductionProvider {
        return "rnnoise";
    }

    private static async getWasmBinary(): Promise<ArrayBuffer> {
        if (!this.wasmBinaryPromise) {
            this.wasmBinaryPromise = fetch(RNNOISE_WASM_URL).then(async (response) => {
                if (!response.ok) {
                    throw new Error(`[RNNoiseWasmReducer][Init][ERROR] Failed to fetch WASM: HTTP ${response.status}`);
                }
                return await response.arrayBuffer();
            });
        }

        try {
            return await this.wasmBinaryPromise;
        } catch (err) {
            this.wasmBinaryPromise = null;
            throw err;
        }
    }

    private static async ensureWorkletLoaded(audioContext: AudioContext): Promise<void> {
        let loadPromise = this.workletLoadPromises.get(audioContext);

        if (!loadPromise) {
            loadPromise = audioContext.audioWorklet.addModule(rnnoiseWorkletUrl).catch((err) => {
                this.workletLoadPromises.delete(audioContext);
                throw err;
            });
            this.workletLoadPromises.set(audioContext, loadPromise);
        }

        await loadPromise;
    }
}
